import { useState } from "react";
import { Plus, Pencil, Trash2, Link2, ExternalLink } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const emptyForm = {
  name: "",
  image_url: "",
  affiliate_link: "",
  source: "internal",
  description: "",
  price_display: "",
  active: true,
  position: 0,
};

const AdminAffiliateProducts = () => {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<any>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const { data: products = [], isLoading } = useQuery({
    queryKey: ["admin-affiliate-products"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("affiliate_products")
        .select("*")
        .order("position", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });

  const upsert = useMutation({
    mutationFn: async (product: typeof emptyForm & { id?: string }) => {
      const payload = { ...product, image_url: product.image_url || null, description: product.description || null, price_display: product.price_display || null };
      if (product.id) {
        const { error } = await (supabase.from("affiliate_products") as any).update(payload).eq("id", product.id);
        if (error) throw error;
      } else {
        const { error } = await (supabase.from("affiliate_products") as any).insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-affiliate-products"] });
      toast.success(editing ? "Product updated" : "Product created");
      setEditing(null);
      setCreating(false);
      setForm(emptyForm);
    },
    onError: (e: any) => toast.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase.from("affiliate_products") as any).delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-affiliate-products"] });
      toast.success("Product removed");
    },
    onError: (e: any) => toast.error(e.message),
  });

  const openCreate = () => { setForm(emptyForm); setEditing(null); setCreating(true); };
  const openEdit = (p: any) => {
    setForm({
      name: p.name,
      image_url: p.image_url || "",
      affiliate_link: p.affiliate_link,
      source: p.source,
      description: p.description || "",
      price_display: p.price_display || "",
      active: p.active,
      position: p.position,
    });
    setEditing(p);
    setCreating(true);
  };

  const handleSave = () => {
    if (!form.name.trim() || !form.affiliate_link.trim()) {
      toast.error("Name and affiliate link are required");
      return;
    }
    upsert.mutate(editing ? { ...form, id: editing.id } : form);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Link2 className="w-6 h-6" /> Affiliate Products
          </h1>
          <p className="text-muted-foreground text-sm">{products.length} product{products.length !== 1 ? "s" : ""}</p>
        </div>
        <Button onClick={openCreate}><Plus className="w-4 h-4 mr-1" /> New Product</Button>
      </div>

      {isLoading ? (
        <div className="text-muted-foreground py-10 text-center">Loading…</div>
      ) : products.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground">
          <Link2 className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p>No affiliate products yet</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((p: any) => (
            <div key={p.id} className="bg-card rounded-lg border p-4 flex flex-col">
              {p.image_url && (
                <img src={p.image_url} alt="" className="w-full h-32 object-cover rounded-lg mb-3" />
              )}
              <p className="font-medium text-sm">{p.name}</p>
              {p.price_display && <p className="text-primary text-sm font-bold">{p.price_display}</p>}
              <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                <span className="capitalize">{p.source}</span>
                {p.source !== "internal" && <ExternalLink className="w-3 h-3" />}
              </div>
              <div className="flex items-center gap-1 mt-auto pt-3">
                <span className={`text-xs ${p.active ? "text-green-600" : "text-muted-foreground"}`}>
                  {p.active ? "Active" : "Inactive"}
                </span>
                <div className="ml-auto flex gap-1">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(p)}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                    onClick={() => { if (window.confirm(`Remove "${p.name}"?`)) deleteMut.mutate(p.id); }}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={creating} onOpenChange={(o) => { if (!o) { setCreating(false); setEditing(null); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Affiliate Product" : "New Affiliate Product"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Product Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Product name" />
            </div>
            <div>
              <Label>Image URL</Label>
              <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://..." />
            </div>
            <div>
              <Label>Affiliate / Product Link *</Label>
              <Input value={form.affiliate_link} onChange={(e) => setForm({ ...form, affiliate_link: e.target.value })} placeholder="https://..." />
            </div>
            <div>
              <Label>Source</Label>
              <Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} placeholder="e.g. Amazon, Internal, AWIN" />
            </div>
            <div>
              <Label>Price Display</Label>
              <Input value={form.price_display} onChange={(e) => setForm({ ...form, price_display: e.target.value })} placeholder="e.g. £29.99" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Short description" rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Display Order</Label>
                <Input type="number" value={form.position} onChange={(e) => setForm({ ...form, position: +e.target.value })} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.active} onCheckedChange={(checked) => setForm({ ...form, active: checked })} />
              <Label>Active</Label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => { setCreating(false); setEditing(null); }}>Cancel</Button>
              <Button onClick={handleSave} disabled={upsert.isPending}>
                {upsert.isPending ? "Saving…" : editing ? "Update" : "Create"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAffiliateProducts;
